// ============================================================================
// CONFIG GROUP SIDEBAR
// ============================================================================
// Left navigation panel showing configuration groups.
// The `icon` field on each ConfigGroup is expected to match an IconSystem key
// directly (e.g. 'settings', 'bell', 'lock') — no separate mapping needed
// as long as your SIMULATED_GROUPS / API data uses the same keys.

import React, { useCallback } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import type { ConfigGroupDto } from 'api/types/config.types';

interface ConfigGroupSidebarProps {
  groups: ConfigGroupDto[];
  activeGroupKey: string | null;
  onGroupSelect: (groupKey: string) => void;
  loading?: boolean;
}

const ConfigGroupSidebar: React.FC<ConfigGroupSidebarProps> = ({
  groups,
  activeGroupKey,
  onGroupSelect,
  loading,
}) => {
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent, groupKey: string) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onGroupSelect(groupKey);
      }
    },
    [onGroupSelect]
  );

  if (loading) {
    return (
      <div className="config-group-sidebar">
        <div className="config-loading">Loading groups...</div>
      </div>
    );
  }

  return (
    <div className="config-group-sidebar">
      {groups.map((group) => {
        const iconKey = group.icon as keyof typeof IconSystem | undefined;
        const IconComponent = iconKey && IconSystem[iconKey] ? IconSystem[iconKey] : null;

        return (
          <div
            key={group.groupKey}
            role="button"
            tabIndex={0}
            className={`config-group-item${activeGroupKey === group.groupKey ? ' active' : ''}`}
            onClick={() => onGroupSelect(group.groupKey)}
            onKeyDown={(e) => handleKeyDown(e, group.groupKey)}
          >
            {IconComponent && (
              <span className="config-group-icon">
                <IconComponent size={18} />
              </span>
            )}
            <span>{group.displayName}</span>
          </div>
        );
      })}
    </div>
  );
};

export default ConfigGroupSidebar;
